/**
 * Notes across a share, planned (decision 76; part 38e log, principles DN1–DN8, tables RK, WN and PN): who reads each key
 * group (an open invite the record gives it, and the relay's list too before a key goes to them); when a group's key is
 * made or rotated, and which epochs go to whom; what a writer's note goes out as; and what a pulled note becomes here.
 * Pure: the clock, the keys' randomness and every read are passed in.
 */
import { CONTEXT, encodeShareNote, MAX_ITEM_TEXT, MAX_MANIFEST_PLACES, MAX_SHARE_NOTE_TITLE, readShareNote } from '@cockatrice/core';
import type { ManifestPlace, ShareNote, TextRange } from '@cockatrice/core';
import { MAX_EPOCH } from '@cockatrice/crypto';
import { KEY_GROUPS } from '@cockatrice/storage';
import type { InviteNotes, InviteRecord, InviteRole, KeyGroup, NoteRecord, NoteShare, RoleKey, ShareRecord } from '@cockatrice/storage';
import { chaptersOf, earlierEnd, inviteOpen, toBase64Id } from './copies.ts';
import type { OwnerList } from './invite-wire.ts';

/** RK14: epoch 0 of a role key is 2026-01-01 (UTC); an epoch is the seconds since, so two devices' rarely meet. */
export const EPOCH_ZERO = Date.UTC(2026, 0, 1);

/** RK15: the most epochs of a group a record keeps (part 38a's `MAX_ROLE_EPOCHS`). */
export const MAX_GROUP_EPOCHS = 64;

/** RK14, Clock: the epoch after `latest` (−1 for none) at `now`: the seconds since {@link EPOCH_ZERO}, past `latest`; null past 2^31 − 1. */
export function epochAfter(latest: number, now: number): number | null {
  const next = Math.max(latest + 1, Math.floor((now - EPOCH_ZERO) / 1000));
  return next > MAX_EPOCH ? null : next;
}

/** IP9: whether an invitee of `role` with `notes` reads key group `group`: their role's and everyone's, unless notes are none. */
export function readsGroup(role: InviteRole, notes: InviteNotes, group: KeyGroup): boolean {
  return notes !== 'none' && (group === 'everyone' || group === role);
}

/** DN3: the readers of a group: those this device gives it (`here`), and of them those the relay's list does too (`keyed`). */
export interface GroupReaders {
  readonly here: ReadonlySet<string>;
  readonly keyed: readonly InviteRecord[];
}

/**
 * DN3: the invites of `record` reading `group` at `now`: open by the relay's list `list` (CS′: listed, its state open,
 * its listed end not past), and given the group by the record's settings; `keyed` also by the listed role and notes.
 */
export function readersOf(record: ShareRecord, list: OwnerList, now: number, group: KeyGroup): GroupReaders {
  const listed = new Map(list.invites.map((invite) => [invite.id, invite]));
  const here = new Set<string>();
  const keyed: InviteRecord[] = [];
  for (const invite of record.invites) {
    const known = listed.get(toBase64Id(invite.id));
    // Fix-next 85, CS⁗: open by both ends, this device's and the listed one.
    if (known === undefined || !inviteOpen({ ...invite, endsAt: earlierEnd(invite.endsAt, known.endsAt) }, known.state, now) || !readsGroup(invite.role, invite.notes, group)) continue;
    here.add(invite.id);
    if (readsGroup(known.role, known.notes, group)) keyed.push(invite);
  }
  return { here, keyed };
}

/** DN5: one epoch of a group to send, and the invites it goes to. */
export interface KeySend {
  readonly group: KeyGroup;
  readonly epoch: number;
  /** The role key, 64 lowercase hex digits. */
  readonly key: string;
  readonly to: readonly InviteRecord[];
}

/** Table RK: the record's role keys as they should stand now, and what to send. */
export interface KeyPlan {
  readonly roleKeys: readonly RoleKey[];
  readonly sends: readonly KeySend[];
}

/** `record`'s keys of `group` (none for `person`, which no role key holds), oldest epoch first. */
export function keysOf(record: Pick<ShareRecord, 'roleKeys'>, group: KeyGroup | 'person'): readonly RoleKey[] {
  return record.roleKeys.filter((key) => key.group === group).sort((a, b) => a.epoch - b.epoch);
}

/**
 * Table RK, DN4, DN5: for each group, a new epoch (its key from `newKey`, no holders) when a holder of its latest no longer
 * reads it here (RK3–RK6, RK8), when a reader comes back (deep-fix r2, table RJ: one its latest withholds), or when it has
 * none and someone reads it or a note waits for it (`needed`, RK1); past {@link MAX_GROUP_EPOCHS} the oldest dropped
 * (RK15); then every epoch held to each keyed reader it wasn't sent to and doesn't withhold (RK2). A new epoch withholds
 * the latest's withheld and the leavers, less everyone reading it now: one who left never gets an epoch made while out.
 */
export function planRoleKeys(record: ShareRecord, list: OwnerList, now: number, needed: ReadonlySet<KeyGroup>, newKey: () => string): KeyPlan {
  const roleKeys: RoleKey[] = [];
  const sends: KeySend[] = [];
  for (const group of KEY_GROUPS) {
    const { here, keyed } = readersOf(record, list, now, group);
    let keys = keysOf(record, group);
    const latest = keys.at(-1);
    // Deep-fix r4, table KP: one a wrap may have reached (its answer lost) counts as a holder when it leaves.
    const leavers = [...(latest?.holders ?? []), ...(latest?.pending ?? [])].filter((holder) => !here.has(holder));
    const back = latest?.withheld.some((invite) => here.has(invite)) === true;
    const rotate = latest === undefined ? keyed.length > 0 || needed.has(group) : leavers.length > 0 || back;
    const epoch = rotate ? epochAfter(latest?.epoch ?? -1, now) : null;
    const withheld = [...new Set([...(latest?.withheld ?? []), ...leavers])].filter((invite) => !here.has(invite));
    if (epoch !== null) keys = [...keys, { group, epoch, key: newKey(), holders: [], withheld, pending: [] }].slice(-MAX_GROUP_EPOCHS);
    roleKeys.push(...keys);
    // RK4, DN4: a group whose holder left but can take no new epoch (a forged record at the last) sends nothing.
    if (rotate && epoch === null) continue;
    for (const key of keys) {
      const to = keyed.filter((invite) => !key.holders.includes(invite.id) && !key.withheld.includes(invite.id));
      if (to.length > 0) sends.push({ group, epoch: key.epoch, key: key.key, to });
    }
  }
  return { roleKeys, sends };
}

/**
 * Deep-fix r4, table KP: `record` with `invites` moved on `group`'s epoch `epoch` as the relay's answer says: `sending`
 * (record first: pending), `taken` (holders, no longer pending) or `refused` (no longer pending).
 */
export function withSent(record: ShareRecord, group: KeyGroup, epoch: number, invites: readonly string[], outcome: 'sending' | 'taken' | 'refused'): ShareRecord {
  const add = (list: readonly string[]): string[] => [...list, ...invites.filter((id) => !list.includes(id))];
  const drop = (list: readonly string[]): string[] => list.filter((id) => !invites.includes(id));
  return {
    ...record,
    roleKeys: record.roleKeys.map((key) => {
      if (key.group !== group || key.epoch !== epoch) return key;
      if (outcome === 'sending') return { ...key, pending: add(key.pending) };
      return outcome === 'taken' ? { ...key, holders: add(key.holders), pending: drop(key.pending) } : { ...key, pending: drop(key.pending) };
    }),
  };
}

/** Table MP: a shared first note on a chapter's passage, where its passage is found now in the chapter's text (null: gone). */
export interface LocatedNote {
  readonly note: NoteRecord;
  readonly shared: NoteShare;
  readonly range: TextRange | null;
}

/**
 * Table MP (MP2, MP5, MP6): the places `invite` of `record` is told in its manifest, of `located` on its given `chapters`:
 * only a note the relay holds (its `sent` a hash: an unsent note's ID never leaves the device, so no reader can take it
 * first), a group note when its settings read the group and the note's epoch doesn't withhold it (RJ), a person's note in
 * its own thread; each relay ID once, by ID, at most {@link MAX_MANIFEST_PLACES}.
 */
export function placesFor(record: Pick<ShareRecord, 'roleKeys'>, invite: InviteRecord, chapters: ReadonlySet<string>, located: readonly LocatedNote[]): readonly ManifestPlace[] {
  const told = located.filter(({ note, shared }) => {
    if (!/^[0-9a-f]{64}$/u.test(shared.sent ?? '') || note.chapterId === null || !chapters.has(note.chapterId)) return false;
    if (note.visibility === 'person') return shared.thread === invite.id;
    if (note.visibility === 'only-me' || !readsGroup(invite.role, invite.notes, note.visibility)) return false;
    return !keysOf(record, note.visibility).some((key) => key.epoch === shared.epoch && key.withheld.includes(invite.id));
  });
  // MP6: two records naming one relay ID (a forged or replayed pull) give it once: the manifest's reader refuses a twice.
  const byNote = new Map<string, ManifestPlace>();
  for (const { shared, range } of told) if (!byNote.has(shared.note)) byNote.set(shared.note, { note: shared.note, range: range === null ? null : { from: range.from, to: range.to } });
  return [...byNote.values()].sort((a, b) => (a.note < b.note ? -1 : 1)).slice(0, MAX_MANIFEST_PLACES);
}

/** Table WN: what a writer's note goes out as: its item, or why it can't (no words yet, WN11 over its bounds). */
export type NoteItem = { readonly kind: 'item'; readonly item: ShareNote; readonly plain: Uint8Array<ArrayBuffer> } | { readonly kind: 'empty' | 'too-large' };

/**
 * WN2, WN11: the item of the writer's `note` (its thread's first note `first`, for a reply): a reply names its thread's
 * first note by its ID at the relay; a first note its chapter and passage, or its Notebook title and section. Deep-fix r2
 * (table AQ): a passage goes as its range alone, never its quote or context, since a group's key reaches readers not given
 * that chapter (and later joiners); one never placed goes as the whole chapter. Deep-fix r3 (table AR): `range` is where
 * the passage is found now in the chapter's text (the caller finds it), so readers are never sent a stale place.
 */
export function itemOfNote(note: NoteRecord, first: NoteRecord | undefined, range: TextRange | null = note.anchor?.range ?? null): NoteItem {
  if (note.text.length > MAX_ITEM_TEXT || note.title.length > MAX_SHARE_NOTE_TITLE || note.group.length > MAX_SHARE_NOTE_TITLE) return { kind: 'too-large' };
  let item: ShareNote;
  if (note.parentId === null) {
    const notebook = note.chapterId === null;
    const placed = notebook || note.anchor === null ? null : range;
    item = { kind: 'note', chapter: note.chapterId, anchor: placed === null ? null : { range: placed, quote: '', before: '', after: '' }, title: notebook ? note.title : '', section: notebook ? note.group : '', text: note.text };
  } else {
    const to = first?.shared?.note;
    if (to === undefined) return { kind: 'empty' };
    item = { kind: 'reply', to, text: note.text };
  }
  const plain = encodeShareNote(item);
  return readShareNote(plain) === null ? { kind: 'empty' } : { kind: 'item', item, plain };
}

/** Where a pulled note sits (table PN), as {@link noteOfPulled} is told it. */
export interface PulledContext {
  /** The note's local ID (DN8). */
  readonly id: string;
  /** Its share part here: the share, its relay ID, thread, author and epoch, and what the relay holds of it. */
  readonly shared: NoteShare;
  readonly visibility: Exclude<NoteRecord['visibility'], 'only-me'>;
  readonly bookId: string;
  /** Its author's invite, when the record holds it (PN13: none, no name). */
  readonly author: InviteRecord | undefined;
  /** For a reply: the first note of the thread it answers, when that note is here (PN4, PN5). */
  readonly first: NoteRecord | undefined;
  /** The live chapters of the book, in order, and every entry's ID (table CS) for the author's range. */
  readonly book: { readonly chapters: readonly { readonly id: string }[]; readonly order: readonly string[] };
  readonly writtenAt: number;
  readonly now: number;
}

/** What a pulled note becomes: its record, and the item that record stands for (what this device would send of it, PN7). */
export interface PulledNoteRecord {
  readonly note: NoteRecord;
  readonly item: ShareNote;
}

/**
 * PN1–PN5, PN7, PN13: the note record pulled `item` becomes: on its chapter at its passage when the book holds that
 * chapter and (for an invitee's) their invite is given it, else in the Notebook; a reply in its thread when the thread's
 * first note here is a standing note of the same share, group and thread, else a first note in the Notebook.
 */
export function noteOfPulled(item: ShareNote, context: PulledContext): PulledNoteRecord {
  const { shared, author } = context;
  const who = shared.author === null ? { origin: 'writer' as const, author: '' } : { origin: 'invitee' as const, author: author?.email ?? '' };
  const base = {
    id: context.id,
    bookId: context.bookId,
    ...who,
    sourceKey: null,
    visibility: context.visibility,
    resolved: false,
    text: item.text,
    writtenAt: context.writtenAt,
    storedAt: context.now,
    editedAt: context.now,
    deletedAt: null,
    shared,
  };
  const notebook = { chapterId: null, anchor: null, parentId: null };
  if (item.kind === 'reply') {
    const { first } = context;
    const inThread =
      first?.parentId === null &&
      first.deletedAt === null &&
      first.bookId === context.bookId &&
      first.shared?.share === shared.share &&
      first.visibility === context.visibility &&
      first.shared.thread === shared.thread;
    if (inThread) return { note: { ...base, chapterId: first.chapterId, anchor: null, title: '', group: '', parentId: first.id }, item: { kind: 'reply', to: first.shared.note, text: item.text } };
    return { note: { ...base, ...notebook, title: '', group: '' }, item: { kind: 'note', chapter: null, anchor: null, title: '', section: '', text: item.text } };
  }
  const given = item.chapter !== null && context.book.chapters.some((chapter) => chapter.id === item.chapter) && (author === undefined ? shared.author === null : chaptersOf(author, context.book).includes(item.chapter));
  if (item.chapter === null || !given) return { note: { ...base, ...notebook, title: item.title, group: item.section }, item: { ...item, chapter: null, anchor: null } };
  const anchor = item.anchor === null ? null : { range: item.anchor.range, quote: item.anchor.quote, before: item.anchor.before.slice(-CONTEXT), after: item.anchor.after.slice(0, CONTEXT) };
  return { note: { ...base, chapterId: item.chapter, anchor, title: '', group: '', parentId: null }, item: { ...item, anchor } };
}
